import { supabase } from '@/lib/supabase';
import type { EventFile, EventFileInsert } from '@/lib/types/database';
import {
  EVENT_FILE_BUCKET,
  EVENT_FILE_SIGNED_URL_EXPIRES_SECONDS,
  type EventFileCountMap,
  type EventFileFormItem,
  type EventFilesSyncInput,
} from '@/lib/types/event-files.types';
import { sanitizeEventStorageFilename } from '@/lib/utils/event-files.utils';
import { createEventFileClientId } from '@/lib/utils/event-files.utils';

export async function getEventFiles(eventId: number): Promise<EventFile[]> {
  const { data, error } = await supabase
    .from('event_files')
    .select('*')
    .eq('event_id', eventId)
    .order('created_at', { ascending: true });

  if (error) {
    throw new Error(`Failed to fetch event files: ${error.message}`);
  }

  return (data as EventFile[]) || [];
}

export async function getEventFileCounts(
  eventIds: number[]
): Promise<EventFileCountMap> {
  if (eventIds.length === 0) {
    return new Map();
  }

  const { data, error } = await supabase
    .from('event_files')
    .select('event_id')
    .in('event_id', eventIds);

  if (error) {
    throw new Error(`Failed to fetch event file counts: ${error.message}`);
  }

  const counts = new Map<number, number>();
  data?.forEach((row) => {
    counts.set(row.event_id, (counts.get(row.event_id) ?? 0) + 1);
  });

  return counts;
}

export async function getEventFileOpenUrl(fileId: string): Promise<string> {
  const { data: file, error: fileError } = await supabase
    .from('event_files')
    .select('*')
    .eq('id', fileId)
    .single();

  if (fileError || !file) {
    throw new Error(
      `Failed to fetch event file: ${fileError?.message || 'File not found'}`
    );
  }

  const { data, error } = await supabase.storage
    .from(EVENT_FILE_BUCKET)
    .createSignedUrl(file.storage_path, EVENT_FILE_SIGNED_URL_EXPIRES_SECONDS, {
      download: file.original_filename,
    });

  if (error || !data?.signedUrl) {
    throw new Error(
      `Failed to open event file: ${error?.message || 'Signed URL unavailable'}`
    );
  }

  return data.signedUrl;
}

export async function syncEventFiles({
  eventId,
  files,
  uploadedByUserId,
}: EventFilesSyncInput): Promise<EventFile[]> {
  if (!uploadedByUserId) {
    throw new Error('User must be authenticated to manage event files');
  }

  const existingFiles = await getEventFiles(eventId);
  const submittedExistingIds = new Set(
    files
      .filter((file) => file.kind === 'existing' && !file.markedForDeletion)
      .map((file) => file.id)
  );
  const filesToDelete = existingFiles.filter(
    (file) => !submittedExistingIds.has(file.id)
  );
  const filesToUpdate = files.filter(
    (file) => file.kind === 'existing' && !file.markedForDeletion
  );
  const filesToCreate = files.filter(
    (file) => file.kind === 'draft' && !file.markedForDeletion && file.file
  );

  if (filesToDelete.length > 0) {
    const { error: deleteRowsError } = await supabase
      .from('event_files')
      .delete()
      .in(
        'id',
        filesToDelete.map((file) => file.id)
      );

    if (deleteRowsError) {
      throw new Error(
        `Failed to delete event files: ${deleteRowsError.message}`
      );
    }

    const { error: deleteStorageError } = await supabase.storage
      .from(EVENT_FILE_BUCKET)
      .remove(filesToDelete.map((file) => file.storage_path));

    if (deleteStorageError) {
      throw new Error(
        `Failed to remove event file uploads: ${deleteStorageError.message}`
      );
    }
  }

  await Promise.all(
    filesToUpdate.map(async (file) => {
      const { error } = await supabase
        .from('event_files')
        .update({
          display_title: file.displayTitle.trim(),
          updated_at: new Date().toISOString(),
        })
        .eq('id', file.id);

      if (error) {
        throw new Error(`Failed to update event file: ${error.message}`);
      }
    })
  );

  const newRows: EventFileInsert[] = [];

  for (const fileItem of filesToCreate) {
    const file = fileItem.file!;
    const storagePath = `${eventId}/${createEventFileClientId()}-${sanitizeEventStorageFilename(file.name)}`;

    const { error: uploadError } = await supabase.storage
      .from(EVENT_FILE_BUCKET)
      .upload(storagePath, file, {
        contentType: 'application/pdf',
        upsert: false,
      });

    if (uploadError) {
      throw new Error(`Failed to upload ${file.name}: ${uploadError.message}`);
    }

    newRows.push({
      event_id: eventId,
      storage_path: storagePath,
      display_title: fileItem.displayTitle.trim(),
      original_filename: fileItem.originalFilename,
      content_type: 'application/pdf',
      size_bytes: fileItem.sizeBytes,
      uploaded_by: uploadedByUserId,
    });
  }

  if (newRows.length > 0) {
    const { error: insertError } = await supabase
      .from('event_files')
      .insert(newRows);

    if (insertError) {
      throw new Error(
        `Failed to save event file records: ${insertError.message}`
      );
    }
  }

  return getEventFiles(eventId);
}

export function getFilesForOccurrence(
  files: EventFileFormItem[],
  occurrenceIndex: number
): EventFileFormItem[] {
  return files
    .filter((file) => !file.markedForDeletion)
    .filter((file) => file.targetOccurrenceIndexes.includes(occurrenceIndex));
}
